/**
 * Billing & feature-gating hooks.
 *
 * Wraps the database functions get_workspace_access() and
 * get_workspace_limits() in TanStack Query hooks so the rest of the app
 * can check access/limits without thinking about RPC plumbing.
 *
 * Two things to know if you're new to this file:
 *
 * 1. Tier vs. access are separate concepts.
 *    - `tier` is one of: free | starter | growth | pro | enterprise
 *    - `hasAccess` is a boolean. False means locked out (canceled subs,
 *      expired trials with no paid plan). When false, treat the workspace
 *      as having no features regardless of tier.
 *    Use `useFeature(name)` if you just want a yes/no — it composes both
 *    correctly so callers don't have to.
 *
 * 2. `null` means unlimited.
 *    `maxUsers` and `maxContacts` are `number | null`. `null` (Enterprise)
 *    means no ceiling. `useLimitCheck` handles this — don't reinvent it
 *    at call sites.
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from './supabase'
import { useAuth } from './auth'

// ─── Types ──────────────────────────────────────────────────────────────────

/** Locked-down to match the workspaces.plan_tier CHECK constraint. */
export type PlanTier = 'free' | 'starter' | 'growth' | 'pro' | 'enterprise'

/**
 * Display names for every workspace tier. Includes non-purchasable tiers
 * (free, enterprise) which the PLANS array in Settings deliberately
 * omits — those are display-only.
 *
 * Use this anywhere the tier is shown to users. Don't use the raw
 * plan_tier string directly; "starter" looks unprofessional and
 * "enterprise" lowercase looks weird, while CSS-only uppercasing
 * doesn't help free or pro names that have non-uniform casing.
 */
export const TIER_NAMES: Record<PlanTier, string> = {
  free: 'Free',
  starter: 'Starter',
  growth: 'Growth',
  pro: 'Pro',
  enterprise: 'Enterprise',
}

/** Reason codes from get_workspace_access(). Diagnostic, may be surfaced in UI. */
export type AccessReason =
  | 'internal'
  | 'trialing'
  | 'past_due'
  | 'incomplete'
  | 'active'
  | 'free_tier'
  | 'trial_expired'
  | 'subscription_canceled'
  | 'no_workspace'

export type WorkspaceAccess = {
  tier: PlanTier
  hasAccess: boolean
  reason: AccessReason
}

export type WorkspaceLimits = {
  /** null means unlimited (enterprise). */
  maxUsers: number | null
  /** null means unlimited (enterprise). */
  maxContacts: number | null
  /** Feature flags this tier has access to. Empty array when locked out. */
  features: string[]
}

/** Feature names the limits API can return. Add new ones here as the product grows. */
export type Feature =
  | 'contacts'
  | 'inbox'
  | 'deals'
  | 'quotes'
  | 'invoices'
  | 'field'
  | 'reputation'
  | 'projects'
  | 'integrations'
  | 'analytics'
  | 'marketing'
  | 'ai'
  | 'fleet'

// ─── Query keys ─────────────────────────────────────────────────────────────
// Centralized so invalidation calls don't drift from query definitions.
// Pattern matches the rest of the codebase: ['<resource>', workspace.id].

export const billingQueryKeys = {
  access: (workspaceId: string | undefined) => ['workspace-access', workspaceId] as const,
  limits: (workspaceId: string | undefined) => ['workspace-limits', workspaceId] as const,
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Returns the workspace's tier, has_access flag, and reason code.
 * The lowest-level primitive — most consumers want useFeature or
 * useLimitCheck instead.
 *
 * Returns the same `useQuery` result shape as the rest of the codebase
 * (`{ data, isLoading, error, ... }`) so it composes naturally.
 */
export function useWorkspaceAccess() {
  const { workspace } = useAuth()

  return useQuery({
    queryKey: billingQueryKeys.access(workspace?.id),
    queryFn: async (): Promise<WorkspaceAccess> => {
      const { data, error } = await supabase.rpc('get_workspace_access')
      if (error) throw error
      // The RPC returns JSONB — Postgres uses snake_case. We translate to
      // camelCase here so the rest of the app uses idiomatic field names.
      const raw = data as { tier: PlanTier; has_access: boolean; reason: AccessReason }
      return {
        tier: raw.tier,
        hasAccess: raw.has_access,
        reason: raw.reason,
      }
    },
    enabled: !!workspace?.id,
  })
}

/**
 * Returns the workspace's limits and feature list.
 * `null` for max* fields = unlimited (enterprise).
 */
export function useWorkspaceLimits() {
  const { workspace } = useAuth()

  return useQuery({
    queryKey: billingQueryKeys.limits(workspace?.id),
    queryFn: async (): Promise<WorkspaceLimits> => {
      const { data, error } = await supabase.rpc('get_workspace_limits')
      if (error) throw error
      const raw = data as { max_users: number | null; max_contacts: number | null; features: string[] }
      return {
        maxUsers: raw.max_users,
        maxContacts: raw.max_contacts,
        features: raw.features ?? [],
      }
    },
    enabled: !!workspace?.id,
  })
}

/**
 * Convenience: returns just the tier as a string, or undefined while loading.
 *
 * Most call sites should use useFeature or useLimitCheck instead — checking
 * tier directly tends to grow into hard-coded ladders ("if pro or enterprise").
 * Use this when you specifically need to render the tier name (e.g., on the
 * Billing page).
 */
export function useWorkspacePlan(): PlanTier | undefined {
  const { data } = useWorkspaceAccess()
  return data?.tier
}

/**
 * Returns whether the workspace has access to a named feature.
 *
 * Composes hasAccess + features.includes correctly, so callers don't have
 * to remember to check both. Returns false during loading and on error
 * (fail-closed for security).
 *
 * Example:
 *   const canSeeAnalytics = useFeature('analytics')
 *   if (!canSeeAnalytics) return <UpgradeCta feature="analytics" />
 */
export function useFeature(feature: Feature): boolean {
  const { data: access } = useWorkspaceAccess()
  const { data: limits } = useWorkspaceLimits()

  if (!access || !limits) return false      // loading or error → deny
  if (!access.hasAccess) return false       // locked out → deny
  return limits.features.includes(feature)
}

/**
 * Returns true if the workspace has access to ANY of the named features.
 * Useful for routes that map to a parent of multiple features (e.g.,
 * /products is accessible if the workspace has quotes OR invoices).
 *
 * Same fail-closed semantics as useFeature: returns false during loading
 * or on error.
 *
 * Example:
 *   const canSeeProducts = useAnyFeature(['quotes', 'invoices'])
 */
export function useAnyFeature(features: Feature[]): boolean {
  const { data: access } = useWorkspaceAccess()
  const { data: limits } = useWorkspaceLimits()

  if (!access || !limits) return false
  if (!access.hasAccess) return false
  return features.some(f => limits.features.includes(f))
}

export type LimitCheck = {
  /** True when currentCount >= limit. False when there's still room or limit is unlimited. */
  atLimit: boolean
  /** Remaining slots before the limit is hit. null when unlimited. Floored at 0. */
  remaining: number | null
  /** The raw limit value. null when unlimited. */
  limit: number | null
  /** True while the underlying limits query is loading. Treat atLimit as conservative during this window. */
  isLoading: boolean
}

/**
 * Compares a current count against a workspace limit.
 * Use this in create-flows to decide whether to block + show an Upgrade CTA.
 *
 * Important: returns `atLimit: true` while loading, to fail-closed. If you
 * want a different loading behavior, use the underlying useWorkspaceLimits
 * directly.
 *
 * Example:
 *   const { atLimit, remaining } = useLimitCheck('contacts', contacts.length)
 *   if (atLimit) return <ContactLimitReachedModal />
 */
export function useLimitCheck(
  field: 'users' | 'contacts',
  currentCount: number,
): LimitCheck {
  const { data: limits, isLoading } = useWorkspaceLimits()

  if (isLoading || !limits) {
    return { atLimit: true, remaining: 0, limit: 0, isLoading: true }
  }

  const limit = field === 'users' ? limits.maxUsers : limits.maxContacts

  // null = unlimited.
  if (limit === null) {
    return { atLimit: false, remaining: null, limit: null, isLoading: false }
  }

  const remaining = Math.max(0, limit - currentCount)
  return { atLimit: currentCount >= limit, remaining, limit, isLoading: false }
}
